import styled from 'styled-components'

import { DataTable, FormGroup, Label, Select } from '@/components'
import Input from '@/components/forms/form-elements/input'
import { FlexWrapper } from '@/components/layout/flex/flexWrapper'
import Button from '@/components/ui/button/button'
import { useAxios } from '@/context/axiosContext'
import { tableStyles } from '@/styles/dataTable/style'
import commonUtils from '@/utils/commonUtils'
import React, { useEffect, useRef, useState } from 'react'
import { TableColumn } from 'react-data-table-component'
import { useLocation, useNavigate } from 'react-router-dom'
import { useRedis } from '@/hooks/useRedis'
import { useDialog } from '@/context/dialogContext'
import { useLoading } from '@/context/loadingContext'
import { areaCd } from '@/constants/areaCd'

declare global {
  interface Window {
    kakao: any
  }
}
interface DataRow {
  cmpnyCd: string
  rnum: string
  bizCd: string
  bizNm: string
  useYn: string
  mshipYn: string
  batchYn: string
  rem: string
  manualYn: string
  userName: string
  updDate: string
  chrgeDomain?: string
  newBatchYn: string
  areaCd: string
  addr: string
  coordinateX: string
  coordinateY: string
  holeType: string
  latestBaseDt: string
  confidenceLvlCd: string
}

interface GetSrchData {
  srchStr?: string //검색어
  srchOpt?: string
  useYn?: string
  mshipYn?: string
  batchYn?: string
  manualYn?: string
  holeType?: string
  newBatchYn?: string
  areaCd?: string
  confidenceLvlCd: string
}

const BizMgmt: React.FC = () => {
  const axios = useAxios()

  const navigate = useNavigate()
  const location = useLocation()
  const { getComCodeValue, getComCodes } = useRedis()

  const [data, setData] = useState<DataRow[]>([])

  /**
   * state로 관리하면 라이브러리 사용 + 데이터 개수가 많아 과부하가 걸리기 때문에
   * ref로 활용함
   */
  const searchInputRef = useRef<HTMLInputElement>(null)
  const srchOptRef = useRef<HTMLSelectElement>(null)
  const useYnRef = useRef<HTMLSelectElement>(null)
  const mshipYnRef = useRef<HTMLSelectElement>(null)
  const batchYnRef = useRef<HTMLSelectElement>(null)
  const manualYnRef = useRef<HTMLSelectElement>(null)
  const holeTypeRef = useRef<HTMLSelectElement>(null)
  const newBatchYnRef = useRef<HTMLSelectElement>(null)
  const areaCdRef = useRef<HTMLSelectElement>(null)
  const confidenceLvlCdRef = useRef<HTMLSelectElement>(null)

  const { showAlert } = useDialog()

  useEffect(() => {
    const bizSearchState = location.state?.bizSearchState || {}

    if (bizSearchState.srchStr && searchInputRef.current)
      searchInputRef.current.value = bizSearchState.srchStr || ''
    if (bizSearchState.srchOpt && srchOptRef.current)
      srchOptRef.current.value = bizSearchState.srchOpt || '01'
    if (bizSearchState.useYn && useYnRef.current)
      useYnRef.current.value = bizSearchState.useYn || ''
    if (bizSearchState.mshipYn && mshipYnRef.current)
      mshipYnRef.current.value = bizSearchState.mshipYn || ''
    if (bizSearchState.batchYn && batchYnRef.current)
      batchYnRef.current.value = bizSearchState.batchYn || ''
    if (bizSearchState.manualYn && manualYnRef.current)
      manualYnRef.current.value = bizSearchState.manualYn || ''
    if (bizSearchState.holeType && holeTypeRef.current)
      holeTypeRef.current.value = bizSearchState.holeType || ''
    if (bizSearchState.newBatchYn && newBatchYnRef.current)
      newBatchYnRef.current.value = bizSearchState.newBatchYn || ''
    if (bizSearchState.areaCd && areaCdRef.current)
      areaCdRef.current.value = bizSearchState.areaCd || ''
    if (bizSearchState.confidenceLvlCd && confidenceLvlCdRef.current)
      confidenceLvlCdRef.current.value = bizSearchState.confidenceLvlCd || ''

    if (bizSearchState.results) {
      setData(bizSearchState.results)
    } else {
      getBizList()
    }
  }, [location.state])

  const getBizList = async (param?: GetSrchData) => {
    const srchStr = param?.srchStr || searchInputRef.current?.value || ''
    const srchOpt = param?.srchOpt || srchOptRef.current?.value || '01'
    const useYn = param?.useYn || useYnRef.current?.value || ''
    const mshipYn = param?.mshipYn || mshipYnRef.current?.value || ''
    const batchYn = param?.batchYn || batchYnRef.current?.value || ''
    const manualYn = param?.manualYn || manualYnRef.current?.value || ''
    const holeType = param?.holeType || holeTypeRef.current?.value || ''
    const newBatchYn = param?.newBatchYn || newBatchYnRef.current?.value || ''
    const areaCd = param?.areaCd || areaCdRef.current?.value || ''
    const confidenceLvlCd =
      param?.confidenceLvlCd || confidenceLvlCdRef.current?.value || ''

    try {
      const response = await axios.get('/bizMgmt/list', {
        params: {
          srchStr,
          srchOpt,
          useYn,
          mshipYn,
          batchYn,
          manualYn,
          holeType,
          newBatchYn,
          areaCd,
          confidenceLvlCd,
        },
      })
      const listData = response.data
      if (Array.isArray(listData)) {
        setData(listData)
      }
    } catch (error) {
      console.error('Error fetching Biz list:', error)
    }
  }

  //수정버튼 클릭시 업체등록/수정으로 이동
  const handleButtonUpdateClick = (id: string) => {
    window.scrollTo(0, 0)
    navigate(`/bizMgmt/detail`, {
      state: {
        bizCd: id,
        bizSearchState: {
          srchStr: searchInputRef.current?.value || '',
          srchOpt: srchOptRef.current?.value || '01',
          useYn: useYnRef.current?.value || '',
          mshipYn: mshipYnRef.current?.value || '',
          batchYn: batchYnRef.current?.value || '',
          manualYn: manualYnRef.current?.value || '',
          holeType: holeTypeRef.current?.value || '',
          areaCd: areaCdRef.current?.value || '',
          newBatchYn: newBatchYnRef.current?.value || '',
          confidenceLvlCd: confidenceLvlCdRef.current?.value || '',
          results: data,
        },
      },
    })
  }

  //가격버튼 클릭시 가격관리로 이동
  const handleButtonPriceClick = (row: DataRow) => {
    window.scrollTo(0, 0)
    navigate(`/bizMgmt/priceMgmt`, {
      state: {
        bizCd: row.bizCd,
        bizNm: row.bizNm,
        chrgeDomain: row.chrgeDomain,
        rem: row.rem,
        bizSearchState: {
          srchStr: searchInputRef.current?.value || '',
          srchOpt: srchOptRef.current?.value || '01',
          useYn: useYnRef.current?.value || '',
          mshipYn: mshipYnRef.current?.value || '',
          batchYn: batchYnRef.current?.value || '',
          manualYn: manualYnRef.current?.value || '',
          holeType: holeTypeRef.current?.value || '',
          areaCd: areaCdRef.current?.value || '',
          newBatchYn: newBatchYnRef.current?.value || '',
          confidenceLvlCd: confidenceLvlCdRef.current?.value || '',
          results: data,
        },
      },
    })
  }

  //스크래핑버튼 클릭시 스크래핑관리로 이동
  const handleButtonScrapClick = (row: DataRow) => {
    window.scrollTo(0, 0)
    navigate(`/bizMgmt/scrpMgmt`, {
      state: {
        bizCd: row.bizCd,
        chrgeDomain: row.chrgeDomain,
        bizSearchState: {
          srchStr: searchInputRef.current?.value || '',
          srchOpt: srchOptRef.current?.value || '01',
          useYn: useYnRef.current?.value || '',
          mshipYn: mshipYnRef.current?.value || '',
          batchYn: batchYnRef.current?.value || '',
          manualYn: manualYnRef.current?.value || '',
          holeType: holeTypeRef.current?.value || '',
          areaCd: areaCdRef.current?.value || '',
          newBatchYn: newBatchYnRef.current?.value || '',
          confidenceLvlCd: confidenceLvlCdRef.current?.value || '',
          results: data,
        },
      },
    })
  }

  //스크래핑NEW버튼 클릭시 스크래핑정보관리 이동
  const handleButtonScrapInfoClick = (row: DataRow) => {
    window.scrollTo(0, 0)
    navigate(`/scrpInfoMgmt`, {
      state: {
        bizCd: row.bizCd,
        bizNm: row.bizNm,
        cmpnyCd: row.cmpnyCd,
        holeType: row.holeType,
        mshipYn: row.mshipYn,
        rem: row.rem,
        bizSearchState: {
          srchStr: searchInputRef.current?.value || '',
          srchOpt: srchOptRef.current?.value || '01',
          useYn: useYnRef.current?.value || '',
          mshipYn: mshipYnRef.current?.value || '',
          batchYn: batchYnRef.current?.value || '',
          manualYn: manualYnRef.current?.value || '',
          holeType: holeTypeRef.current?.value || '',
          areaCd: areaCdRef.current?.value || '',
          newBatchYn: newBatchYnRef.current?.value || '',
          confidenceLvlCd: confidenceLvlCdRef.current?.value || '',
          results: data,
        },
      },
    })
  }

  const handleRegClick = () => {
    window.scrollTo(0, 0)
    navigate('/bizMgmt/detail')
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      getBizList()
    }
  }

  const { startLoading, stopLoading } = useLoading()

  const getCoordinates = () => {
    const noCoordinateList = data
      .filter((item) => !item.coordinateX || !item.coordinateY)
      .slice(0, 50)
    const successList: DataRow[] = []
    const failList: DataRow[] = []
    const geocoder = new window.kakao.maps.services.Geocoder()

    startLoading()

    noCoordinateList.forEach((item, index) => {
      setTimeout(() => {
        geocoder.addressSearch(item.addr, (result: any, status: any) => {
          if (status === window.kakao.maps.services.Status.OK) {
            item.coordinateX = result[0].x
            item.coordinateY = result[0].y
            successList.push(item)
            if (index == noCoordinateList.length - 1) {
              stopLoading()
              updateCoordinateList(successList)
            }
          } else {
            if (status) {
              console.log(
                item.bizNm +
                  ' 좌표 정보 가져오기 실패(' +
                  status +
                  ')-' +
                  item.addr,
              )
            }
            if (index == noCoordinateList.length - 1) {
              stopLoading()
            }
          }
        })
      }, 100 * index)
    })
  }

  const updateCoordinateList = async (successList: DataRow[]) => {
    // const chunks:any[] = [];
    // const size = 50;
    // for (let i = 0; i < successList.length; i += size) {
    //   chunks.push(successList.slice(i, i + size));
    // }
    const result = await axios.post('/bizMgmt/updateCoordinateList', {
      list: successList,
    })
    if (result.status == 200) {
      showAlert({
        title: '좌표 일괄등록 성공',
        type: 'success',
      })
    } else {
      showAlert({
        title: '좌표정보 일괄등록 실패',
        type: 'error',
      })
    }
  }

  const columns: TableColumn<DataRow>[] = [
    {
      name: 'No.',
      selector: (row) => parseInt(row.rnum, 10),
      sortable: true,
      minWidth: '40px',
      maxWidth: '60px',
      cell: (row) => (
        <div style={{ width: '100%', textAlign: 'right' }}>{row.rnum}</div>
      ),
    },
    /* 
    {
      name: '업체코드',
      selector: (row) => row.bizCd,
      sortable: true,
      center: true,
    },
    */
    {
      name: '업체명',
      selector: (row) => row.bizNm,
      cell: (row) => {
        return (
          <a
            href={row.chrgeDomain}
            rel="noopener noreferrer"
            target="_blank"
            title={`${row.bizNm} : ${row.bizCd}`}
          >
            {row.bizNm}
          </a>
        )
      },
      sortable: true,
      wrap: true,
      grow: 1,
    },
    {
      name: '사용여부',
      selector: (row) => getComCodeValue('USE_YN', row.useYn)?.codeNm || '',
      sortable: true,
      minWidth: '80px',
      maxWidth: '80px',
      center: true,
    },
    {
      name: '회원제여부',
      selector: (row) => getComCodeValue('MSHIP_YN', row.mshipYn)?.codeNm || '',
      sortable: true,
      minWidth: '100px',
      maxWidth: '100px',
      center: true,
    },
    // {
    //   name: '배치여부',
    //   selector: (row) => getComCodeValue('BATCH_YN', row.batchYn)?.codeNm || '',
    //   sortable: true,
    //   minWidth: '80px',
    //   maxWidth: '80px',
    //   center: true,
    // },
    {
      name: '배치여부',
      selector: (row) =>
        getComCodeValue('NEW_BATCH_YN', row.newBatchYn)?.codeNm || '',
      sortable: true,
      minWidth: '80px',
      maxWidth: '80px',
      center: true,
    },
    {
      name: '수기관리여부',
      selector: (row) =>
        getComCodeValue('MANUAL_YN', row.manualYn)?.codeNm || '',
      sortable: true,
      minWidth: '110px',
      maxWidth: '110px',
      center: true,
    },
    {
      name: '홀타입',
      selector: (row) =>
        getComCodeValue('HOLE_TYPE', row.holeType)?.codeNm || '',
      sortable: true,
      minWidth: '80px',
      maxWidth: '80px',
      center: true,
    },
    {
      name: '정확도',
      selector: (row) =>
        getComCodeValue('CONFIDENCE_LVL_CD', row.confidenceLvlCd)?.codeNm || '',
      sortable: true,
      minWidth: '80px',
      maxWidth: '80px',
      center: true,
    },
    {
      name: '가격기준일자',
      selector: (row) => row.latestBaseDt,
      format: (row) => commonUtils.formatDate(row.latestBaseDt),
      sortable: true,
      minWidth: '110px',
      maxWidth: '110px',
      center: true,
    },
    {
      name: '비고',
      selector: (row) => row.rem,
      cell: (row) => (
        <div
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '150px', // 꼭 필요! 없으면 줄지 않음
          }}
          title={row.rem}
        >
          {row.rem}
        </div>
      ),
      sortable: true,
      wrap: true,
      grow: 1,
    },
    {
      name: '수정자',
      selector: (row) => row.userName,
      sortable: true,
      center: true,
    },
    {
      name: '수정일시',
      selector: (row) => row.updDate,
      format: (row) => commonUtils.formatDate(row.updDate),
      sortable: true,
    },
    {
      name:
        // <Button
        //   onClick={() => {
        //     getCoordinates();
        //   }}
        //   color="neverbe"
        //   variant="outlined"
        // >
        //   📌
        // </Button>
        '',
      cell: (row) => (
        <FlexWrapper gap="20px">
          <Button
            variant="outlined"
            color="neverbe"
            size="xs"
            onClick={() => handleButtonUpdateClick(row.bizCd)}
          >
            수정
          </Button>
          <Button
            variant="outlined"
            color="neverbe"
            size="xs"
            onClick={() => handleButtonPriceClick(row)}
          >
            가격
          </Button>
          <Button
            variant="outlined"
            color="neverbe"
            size="xs"
            onClick={() => {
              window.open('/priceIntgMgmt?bizNm=' + row.bizNm, '_blank')
            }}
          >
            통합관리
          </Button>
          <Button
            variant="outlined"
            color="neverbe"
            size="xs"
            onClick={() => handleButtonScrapInfoClick(row)}
          >
            스크래핑NEW
          </Button>
          {/* 
          <Button
            variant="outlined"
            color="neverbe"
            size="xs"
            onClick={() => handleButtonScrapClick(row)}
          >
            스크래핑
          </Button>
          */}
        </FlexWrapper>
      ),
      minWidth: '470px',
      sortable: false,
    },
  ]

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          marginBottom: '10px',
          alignItems: 'center',
          marginTop: '50px',
        }}
      >
        <p style={{ marginLeft: '20px', fontSize: '20px' }}>업체관리</p>
        <div style={{ marginLeft: 'auto' }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <FormGroup>
              <Label htmlFor={'useYn'}>사용여부</Label>
              <Select id="useYn" name="useYn" width="90px" ref={useYnRef}>
                <option value="">전체</option>
                {getComCodes('USE_YN').map((item) => (
                  <option key={'useYn' + item.code} value={item.code}>
                    {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor={'mshipYn'}>회원제여부</Label>
              <Select id="mshipYn" name="mshipYn" width="90px" ref={mshipYnRef}>
                <option value="">전체</option>
                {getComCodes('MSHIP_YN').map((item) => (
                  <option key={'mshipYn' + item.code} value={item.code}>
                    {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>

            {/* <FormGroup>
              <Label htmlFor={'batchYn'}>배치여부</Label>
              <Select id="batchYn" name="batchYn" width="90px" ref={batchYnRef}>
                <option value="">전체</option>
                {getComCodes('BATCH_YN').map((item) => (
                  <option key={'batchYn' + item.code} value={item.code}>
                    {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup> */}
            <FormGroup>
              <Label htmlFor={'newBatchYn'}>배치여부</Label>
              <Select
                id="newBatchYn"
                name="newBatchYn"
                width="90px"
                ref={newBatchYnRef}
              >
                <option value="">전체</option>
                {getComCodes('NEW_BATCH_YN').map((item) => (
                  <option key={'newBatchYn' + item.code} value={item.code}>
                    {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor={'manualYn'}>수기관리여부</Label>
              <Select
                id="manualYn"
                name="manualYn"
                width="90px"
                ref={manualYnRef}
              >
                <option value="">전체</option>
                {getComCodes('MANUAL_YN').map((item) => (
                  <option key={'manualYn' + item.code} value={item.code}>
                    {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label htmlFor={'holeType'}>홀타입</Label>
              <Select
                id="holeType"
                name="holeType"
                width="90px"
                ref={holeTypeRef}
              >
                <option value="">전체</option>
                {getComCodes('HOLE_TYPE').map((item) => (
                  <option key={'holeType' + item.code} value={item.code}>
                    {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor={'confidenceLvlCd'}>정확도</Label>
              <Select
                id="confidenceLvlCd"
                name="confidenceLvlCd"
                width="90px"
                ref={confidenceLvlCdRef}
              >
                <option value="">전체</option>
                {getComCodes('CONFIDENCE_LVL_CD').map((item) => (
                  <option key={'confidenceLvlCd' + item.code} value={item.code}>
                    {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor={'areaCd'}>지역별</Label>
              <Select id="areaCd" name="areaCd" width="130px" ref={areaCdRef}>
                <option value="">전체</option>
                {getComCodes('AREA_CD').map((item) => (
                  <option key={'areaCd' + item.code} value={item.code}>
                    {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor={'srchOpt'}>
                <></>
              </Label>
              <FlexWrapper gap="10px">
                <Select
                  id="srchOpt"
                  name="srchOpt"
                  width="150px"
                  ref={srchOptRef}
                >
                  <option value="01">업체명</option>
                  <option value="02">업체코드</option>
                  <option value="03">비고</option>
                  <option value="04">고객용비고</option>
                </Select>
                <Input
                  placeholder="검색어"
                  width="300px"
                  id="srchStr"
                  name="srchStr"
                  ref={searchInputRef} // Ref 연결
                  maxLength={100}
                  onKeyDown={handleKeyDown}
                />
              </FlexWrapper>
            </FormGroup>
            <FormGroup>
              <Label htmlFor={''}>
                <></>
              </Label>
              <FlexWrapper gap="10px">
                <Button onClick={getBizList} color="neverbe" variant="outlined">
                  검색
                </Button>
                <Button onClick={handleRegClick} color="neverbe">
                  등록
                </Button>
              </FlexWrapper>
            </FormGroup>
          </div>
        </div>
      </div>

      <BizSearchContainerWrapper>
        <DataTable
          columns={columns}
          data={data}
          customStyles={tableStyles}
          defaultSortAsc={false}
          noDataComponent={<NoDataComponent />}
        />
        {data && data.length > 0 && (
          <p
            style={{
              marginLeft: 'auto',
              marginRight: '10px',
              marginTop: '10px',
            }}
          >
            총 <b>{data.length}</b> 건
          </p>
        )}
      </BizSearchContainerWrapper>
    </>
  )
}

const BizSearchContainerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 700px;
`

export const NoDataComponent = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '50vh',
      backgroundColor: '#ffffff',
      borderRadius: '8px',
      textAlign: 'center',
      padding: '40px 10px',
      margin: '20px 0',
    }}
  >
    <p
      style={{
        fontSize: '18px',
        fontWeight: '500',
        color: '#666',
        marginBottom: '20px',
      }}
    >
      조회 결과가 없어요.
    </p>
  </div>
)

export default BizMgmt
